import React, { useRef, useState } from 'react'
import { Paperclip, Plus, X } from 'lucide-react'
import { FILE_INPUT_ACCEPT } from '../config'
import {
  formatFileSize,
  formatTotalSize,
  getFileIconSmall,
  validateFiles,
} from '../hooks/utils'
import type { AttachmentPickerProps } from '../types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Compact attachment picker for use in task creation forms
 * Shows inline file list that expands when files are added
 */
export const AttachmentPicker: React.FC<AttachmentPickerProps> = ({
  files,
  onFilesChange,
  disabled = false,
  onClickUpload,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleFileInputClick = () => {
    // Call optional click handler before opening file picker
    if (onClickUpload) {
      const shouldContinue = onClickUpload()
      if (!shouldContinue) {
        return
      }
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    const { validFiles, errors } = validateFiles(selectedFiles, files)

    if (errors.length > 0) {
      setErrorMessage(errors.join('\n'))
    }

    if (validFiles.length > 0) {
      onFilesChange([...files, ...validFiles])
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    onFilesChange(newFiles)
  }

  return (
    <div className="flex items-start gap-2 w-full">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
        accept={FILE_INPUT_ACCEPT}
        aria-label="Select files to attach"
      />

      {files.length === 0 ? (
        // Empty state - compact icon button
        <button
          type="button"
          onClick={handleFileInputClick}
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground hover:bg-accent rounded p-1 transition-colors disabled:opacity-50"
          title="Add attachments"
        >
          <Paperclip className="w-4 h-4" />
        </button>
      ) : (
        // Files selected - expanded view
        <div className="flex-1 space-y-1.5 min-w-0">
          {/* Header with file count */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleFileInputClick}
              disabled={disabled}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <Paperclip className="w-4 h-4" />
              {files.length} {files.length === 1 ? 'file' : 'files'} (
              {formatTotalSize(files)})
            </button>
          </div>

          {/* File list */}
          <div className="space-y-1.5 bg-muted border border-border rounded-md p-2 w-full">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 p-2 bg-card rounded border border-border hover:border-muted-foreground/40 transition-colors group min-w-0"
              >
                <div className="flex-shrink-0">
                  {getFileIconSmall(file.type)}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p
                    className="text-xs font-medium text-foreground truncate"
                    title={file.name}
                  >
                    {file.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  disabled={disabled}
                  className="flex-shrink-0 p-1 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50"
                  title="Remove file"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}

            {/* Add more button */}
            <button
              type="button"
              onClick={handleFileInputClick}
              disabled={disabled}
              className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors w-full disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" />
              Add more files
            </button>
          </div>
        </div>
      )}

      {/* Error Dialog */}
      <AlertDialog
        open={!!errorMessage}
        onOpenChange={(open) => !open && setErrorMessage(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Add Files</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrorMessage(null)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
